import type { Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IEvaluationProject, IProjectNote, IProjectParticipant, IResolvedParticipant } from 'Model/Project/IProject.js'
import type { IBulkResponse } from '../../../Framework/Repository/Response/IBulkResponse.js'
import type { ProjectWithCounts } from '../../../Model/Project/ProjectWithCounts.js'
import type { IProjectSample, ProjectOrder } from '../../../Model/Project/IProjectSample.js'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import type { BlastStatistics } from '../../../Model/Project/IOtuProcessIdDTO.js'
import type { IProjectInvite } from '../../../Model/Invite/IInvite.js'
import type { OpBatch } from '../../../View/Page/HomePage/Modals/MapToBatches.js'
import type { SampleOperationResponse } from '../../../View/Page/HomePage/Modals/CSVModalTypes.js'
import { type Sample } from '../../../Model/index.js'
import type { ISampleMetaData } from '../../../Model/Sample/ISampleMetaData.js'
import type { WSMessageCauser } from '../../Epic/Websocket/WSMessageCauser.js'
import { removeRequest } from 'Flux/FluxUtils.ts'
import { trackProjectAction } from 'Services/Telemetry/index.ts'
import type { RedlistModeKey } from '../../../Generated/RedlistMode.ts'
import type { GroupModeKey } from '../../../Generated/GroupMode.ts'
import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import type { ReportRequest } from '../../../Generated/ReportRequest.ts'
import type { ProjectSortValue } from '../../../Model/Project/ProjectSort.js'

export interface ProjectState {
  openRequests: string[]
  /** Whether a create is in flight, so the modal can stay up until it settles either way. */
  creatingProject: boolean
  currentProject?: IEvaluationProject
  /** The project whose fetch answered 404: it does not exist, or the user may not see it. */
  notFoundProjectId?: string
  currentSamples: IProjectSample[] | 'LOADING'
  /** The project `currentSamples` were requested for. */
  samplesProjectId?: string
  samplesNextOffset: number | null
  samplesLoading: boolean
  projects: ProjectWithCounts[] | 'LOADING'
  participants: Record<string, IResolvedParticipant[]> | 'LOADING'
  notes: Record<string, IProjectNote[]> | 'LOADING'
  blastStatistics: Record<string, Record<BlastSource, BlastStatistics>>
  total: number
  projectOrders: ProjectOrder[] | 'LOADING'
  /** The project `projectOrders` were requested for. */
  projectOrdersProjectId?: string
  totalSamples: number
}

const initialState: ProjectState = {
  openRequests: [],
  creatingProject: false,
  projects: 'LOADING',
  participants: 'LOADING',
  notes: 'LOADING',
  total: 0,
  currentSamples: 'LOADING',
  samplesNextOffset: 0,
  samplesLoading: false,
  blastStatistics: {},
  projectOrders: 'LOADING',
  totalSamples: -1
}

function maskLoading<T>(value: T | 'LOADING', base: T): T {
  if (value === 'LOADING') {
    return base
  }
  return value
}

const reducers = {
  loadProjectOrdersRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.openRequests.push(action.type)
    state.projectOrders = 'LOADING'
    state.projectOrdersProjectId = action.payload.projectId
  },
  loadProjectOrdersRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<ProjectOrder[]>) => {
    state.openRequests = removeRequest(state, action)
    state.projectOrders = action.payload
  },
  loadProjectOrdersRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  toggleDraftModeRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; draftMode: boolean; version: string }>
  ) => {
    state.openRequests.push(action.type)
  },
  toggleDraftModeSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; draftMode: boolean; version: string }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.currentProject === undefined) {
      throw new Error('invalid project slice state')
    }
    state.currentProject.draftMode = action.payload.draftMode
    trackProjectAction(action.payload.draftMode ? 'draft_enabled' : 'activated', action.payload.projectId)
  },
  toggleDraftModeFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  getReportRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectName: string; projectId: string; settings: ReportRequest }>
  ) => {
    state.openRequests.push(action.type)
  },
  getReportRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; link: string }>) => {
    // The project's counterpart to `report.download` on an order: the merged cross-order XLSX is
    // the one thing a project produces for someone outside it, so it is the deliverable to watch.
    trackProjectAction('report_downloaded', action.payload.projectId)
    state.openRequests = removeRequest(state, action)
  },
  getReportRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  batchUpdateSamples: (state: Draft<ProjectState>, action: PayloadAction<OpBatch>) => {
    state.openRequests.push(action.type)
  },
  batchUpdateSamplesSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{
      batch: OpBatch
      result: SampleOperationResponse
    }>
  ) => {
    state.openRequests = removeRequest(state, action)
    if (state.currentSamples === 'LOADING') {
      throw new Error('invalid project slice state')
    }
    /* 1. adjust samples */
    action.payload.result.samples.forEach((sample) => {
      if (state.currentSamples === 'LOADING') {
        throw Error('invalid project slice state')
      }
      state.currentSamples.filter((x) => x.sampleId === sample.id).forEach((x) => (x.sample = sample as Sample))
    })

    const allMeta: ISampleMetaData[] = []
    allMeta.push(...action.payload.result.updatedMetadata)
    allMeta.push(...action.payload.result.newMetadata)
    allMeta.forEach((meta) => {
      if (state.currentSamples === 'LOADING') {
        throw Error('invalid project slice state')
      }
      state.currentSamples
        .filter((x) => x.sampleId === meta.sampleId)
        .forEach((x) => {
          x.sample.sampleMetadata = meta
          x.sample.customerSampleName = meta.customerSampleName
        })
    })
  },
  batchUpdateSampleFailure: (state: Draft<ProjectState>, action: PayloadAction<Error>) => {
    state.openRequests = removeRequest(state, action)
  },
  addProjectSamplesRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; samples: string[] }>) => {
    state.openRequests.push(action.type)
  },
  addProjectSamplesRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IProjectSample[]>) => {
    state.openRequests = removeRequest(state, action)
    state.currentSamples = [...maskLoading(state.currentSamples, []), ...action.payload]
    state.totalSamples = state.totalSamples + action.payload.length
    const projectId = action.payload[0]?.projectId
    if (projectId) trackProjectAction('sample_added', projectId, { count: action.payload.length })
  },
  addProjectSampleRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deleteProjectSamplesRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; projectSampleId: number }>) => {
    state.openRequests.push(action.type)
  },
  deleteProjectSamplesRequestSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; projectSampleId: number }>
  ) => {
    state.openRequests = removeRequest(state, action)
    state.currentSamples = maskLoading(state.currentSamples, []).filter((x) => x.id !== action.payload.projectSampleId)
    state.totalSamples = state.totalSamples - 1
    trackProjectAction('sample_removed', action.payload.projectId, { count: 1 })
  },
  deleteProjectSamplesRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },
  deleteMultipleProjectSamplesRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; projectSampleIds: number[] }>
  ) => {
    state.openRequests.push(action.type)
  },
  deleteMultipleProjectSamplesRequestSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; projectSampleIds: number[] }>
  ) => {
    state.openRequests = removeRequest(state, action)
    state.currentSamples = maskLoading(state.currentSamples, []).filter((x) => !action.payload.projectSampleIds.includes(x.id))
    state.totalSamples = state.totalSamples - action.payload.projectSampleIds.length
    trackProjectAction('sample_removed', action.payload.projectId, { count: action.payload.projectSampleIds.length })
  },
  deleteMultipleProjectSamplesRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    state.openRequests = removeRequest(state, action)
  },

  // Project actions
  getProjectSamplesRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; pageSize?: number }>) => {
    state.openRequests.push(action.type)
    state.currentSamples = 'LOADING'
    state.samplesProjectId = action.payload.projectId
    state.samplesLoading = true
    state.samplesNextOffset = 0
    state.totalSamples = -1
  },
  /* not named *success to avoid numerous popups */
  getProjectSamplesRequestComplete: (
    state: Draft<ProjectState>,
    action: PayloadAction<{
      items: IProjectSample[]
      nextOffset: number | null
      append: boolean
      total: number
    }>
  ) => {
    if (state.currentSamples === 'LOADING') {
      state.currentSamples = []
    }
    state.currentSamples = action.payload.append ? [...state.currentSamples, ...(action.payload.items ?? [])] : (action.payload.items ?? [])
    state.samplesNextOffset = action.payload.nextOffset
    state.samplesLoading = action.payload.nextOffset != null
    state.openRequests = removeRequest(state, action)
    state.totalSamples = action.payload.total
  },
  getProjectSamplesRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  // Project actions
  getProjectRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.notFoundProjectId = undefined
    state.openRequests.push(action.type)
  },
  getProjectRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IEvaluationProject>) => {
    state.currentProject = action.payload
    state.openRequests = removeRequest(state, action)
  },
  getProjectRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error; projectId: string; notFound: boolean }>) => {
    console.error(action.payload.error)
    if (action.payload.notFound) {
      state.notFoundProjectId = action.payload.projectId
    }
    state.openRequests = removeRequest(state, action)
  },

  getAllProjectsRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{
      size: number
      page: number
      filters: { parameter: string; value: unknown; filterType: string }[]
      sort?: ProjectSortValue
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  getAllProjectsRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IBulkResponse<ProjectWithCounts>>) => {
    state.projects = action.payload.content
    state.total = action.payload.totalSize
    state.openRequests = removeRequest(state, action)
  },
  getAllProjectsRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  createProjectRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{
      targetTaxonomy?: string
      name: string
      description: string
      redlistMode?: RedlistModeKey
      groupingMode?: GroupModeKey
      processingType?: ProcessingTypeKey
    }>
  ) => {
    state.openRequests.push(action.type)
    state.creatingProject = true
  },
  /**
   * The list is server-sorted, filtered and paged, so a row placed here can only be right by luck:
   * appending put a new project at the *bottom* of a newest-first page, one row past the page size,
   * with `total` still reporting the old count. The caller navigates to the project instead, and the
   * list refetches whenever it is mounted.
   */
  createProjectRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IEvaluationProject>) => {
    state.currentProject = action.payload
    state.creatingProject = false
    state.openRequests = removeRequest(state, action)
    trackProjectAction('created', action.payload.id)
  },
  createProjectRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.creatingProject = false
    state.openRequests = removeRequest(state, action)
  },
  updateProjectRequest: (state: Draft<ProjectState>, action: PayloadAction<IEvaluationProject>) => {
    state.openRequests.push(action.type)
  },
  updateProjectRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IEvaluationProject>) => {
    state.currentProject = action.payload
    state.projects = maskLoading(state.projects, []).map((entry) =>
      entry.project.id === action.payload.id ? { ...entry, project: action.payload } : entry
    )
    trackProjectAction('edited', action.payload.id)
    state.openRequests = removeRequest(state, action)
  },
  updateProjectRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  deleteProjectRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.openRequests.push(action.type)
  },
  deleteProjectRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.currentProject = undefined
    state.projects = maskLoading(state.projects, []).filter((entry) => entry.project.id !== action.payload.projectId)
    state.openRequests = removeRequest(state, action)
  },
  deleteProjectRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  // Participant actions
  getParticipantsRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.openRequests.push(action.type)
  },
  getParticipantsRequestSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; participants: IResolvedParticipant[] }>
  ) => {
    if (state.participants == 'LOADING') {
      state.participants = {}
    }
    state.participants[action.payload.projectId] = action.payload.participants
    state.openRequests = removeRequest(state, action)
  },
  getParticipantsRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  inviteProjectParticipantRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{
      projectId: string
      participantEmail: string
      additionalText: string
      mayEdit: boolean
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  inviteProjectParticipantRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IProjectInvite>) => {
    state.openRequests = removeRequest(state, action)
    trackProjectAction('invite_sent', action.payload.projectId, { may_edit: action.payload.mayEdit })
  },
  inviteProjectParticipantRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  acceptProjectInviteRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; inviteId: number }>) => {
    state.openRequests.push(action.type)
  },
  acceptProjectInviteRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<IProjectInvite>) => {
    state.openRequests = removeRequest(state, action)
    trackProjectAction('invite_accepted', action.payload.projectId)
  },
  acceptProjectInviteRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  updateParticipantRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; participantId: number; mayEdit: boolean; version: string }>
  ) => {
    state.openRequests.push(action.type)
  },
  updateParticipantRequestSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; participant: IProjectParticipant }>
  ) => {
    const projectId = action.payload.projectId
    if (state.participants == 'LOADING') {
      state.participants = {}
    }
    const participants = state.participants[projectId] || []
    state.participants[projectId] = participants.map((p) =>
      p.participant.id === action.payload.participant.id ? { user: p.user, participant: action.payload.participant } : p
    )
    state.openRequests = removeRequest(state, action)
    trackProjectAction('permission_changed', projectId, { may_edit: action.payload.participant.mayEdit })
  },
  updateParticipantRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },
  removeParticipantRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; participantId: number }>) => {
    state.openRequests.push(action.type)
  },
  removeParticipantRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; participantId: number }>) => {
    const projectId = action.payload.projectId
    if (state.participants == 'LOADING') {
      state.participants = {}
    }
    const participants = state.participants[projectId] || []
    state.participants[projectId] = participants.filter((p) => p.participant.id !== action.payload.participantId)
    state.openRequests = removeRequest(state, action)
    trackProjectAction('participant_removed', projectId)
  },
  removeParticipantRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  // Note actions
  getNotesRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string }>) => {
    state.openRequests.push(action.type)
  },
  getNotesRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; notes: IProjectNote[] }>) => {
    if (state.notes == 'LOADING') {
      state.notes = {}
    }
    state.notes[action.payload.projectId] = action.payload.notes
    state.openRequests = removeRequest(state, action)
  },
  getNotesRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  addNoteRequest: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; note: Omit<IProjectNote, 'id' | 'createdAt'> }>
  ) => {
    state.openRequests.push(action.type)
  },
  addNoteRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; note: IProjectNote }>) => {
    const projectId = action.payload.projectId
    if (state.notes == 'LOADING') {
      state.notes = {}
    }
    const notes = state.notes[projectId] || []
    state.notes[projectId] = [...notes, action.payload.note]
    state.openRequests = removeRequest(state, action)
  },
  addNoteRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  deleteNoteRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; noteId: number }>) => {
    state.openRequests.push(action.type)
  },
  deleteNoteRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; noteId: number }>) => {
    const projectId = action.payload.projectId
    if (state.notes == 'LOADING') {
      state.notes = {}
    }
    const notes = state.notes[projectId] || []
    state.notes[projectId] = notes.filter((n) => n.id !== action.payload.noteId)
    state.openRequests = removeRequest(state, action)
  },
  deleteNoteRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  updateNoteRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; noteId: number; content: string }>) => {
    state.openRequests.push(action.type)
  },
  updateNoteRequestSuccess: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; note: IProjectNote }>) => {
    const projectId = action.payload.projectId
    if (state.notes == 'LOADING') {
      state.notes = {}
    }
    const notes = state.notes[projectId] || []
    state.notes[projectId] = notes.map((n) => (n.id === action.payload.note.id ? action.payload.note : n))
    state.openRequests = removeRequest(state, action)
  },
  updateNoteRequestFailure: (state: Draft<ProjectState>, action: PayloadAction<{ error: Error }>) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)
  },

  // OTU Process ID actions
  getOtuProcessIdsRequest: (state: Draft<ProjectState>, action: PayloadAction<{ projectId: string; blastType: BlastSource }>) => {
    state.openRequests.push(action.type)

    // Initialize the project's blast statistics if it doesn't exist
    if (!state.blastStatistics[action.payload.projectId]) {
      state.blastStatistics[action.payload.projectId] = {} as Record<BlastSource, BlastStatistics>
    }

    // Set the loading state for this blast type
    state.blastStatistics[action.payload.projectId][action.payload.blastType] = {
      loading: true,
      /* keep previous data to blank out loading */
      data: state.blastStatistics[action.payload.projectId][action.payload.blastType]?.data
    }
  },

  getOtuProcessIdsRequestSuccess: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; blastType: BlastSource; data: { blastIds: string[]; missedOtus: number } }>
  ) => {
    state.openRequests = removeRequest(state, action)

    // Ensure the project's blast statistics exists
    if (!state.blastStatistics[action.payload.projectId]) {
      state.blastStatistics[action.payload.projectId] = {} as Record<BlastSource, BlastStatistics>
    }

    // Update the blast statistics for this blast type
    state.blastStatistics[action.payload.projectId][action.payload.blastType] = {
      loading: false,
      data: {
        blastIds: action.payload.data.blastIds,
        missedOtus: action.payload.data.missedOtus
      }
    }
  },

  getOtuProcessIdsRequestFailure: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; blastType: BlastSource; error: Error }>
  ) => {
    console.error(action.payload.error)
    state.openRequests = removeRequest(state, action)

    // Ensure the project's blast statistics exists
    if (!state.blastStatistics[action.payload.projectId]) {
      state.blastStatistics[action.payload.projectId] = {} as Record<BlastSource, BlastStatistics>
    }

    // Update the blast statistics for this blast type with the error
    state.blastStatistics[action.payload.projectId][action.payload.blastType] = {
      loading: false,
      error: action.payload.error
    }
  },

  wsProjectLockChanged: (
    state: Draft<ProjectState>,
    action: PayloadAction<{ projectId: string; causer: WSMessageCauser; locked: boolean }>
  ) => {
    console.info('lock changed ', action.payload, state.currentProject?.id)
  },

  reset: () => {
    return initialState
  }
}

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers
})
